<template>
  <div>
    <PageTitle :title="$t('global.add-cloud-server')" />
    <div class="row">
      <div class="col-md-8">
        <Card :disable-title="true">
          <LoadingState v-if="loading" />
          <template v-else>
            <!-- select location -->
            <div class="row mb-4">
              <div class="col-md-12">
                <label for="select-location">{{ $t("global.select-location") }} *</label>
                <div id="select-location">
                  <FormSelect
                    name="location"
                    v-model="fields.locationId"
                    test-id="location-input"
                    :disabled="isSubmitting"
                    :errors="validation.fields.locationId.errors"
                    :dirty="validation.fields.locationId.dirty"
                  >
                    <option v-if="!fields.locationId" selected="true" value="">
                      - {{ $t("global.select-location") }} -
                    </option>
                    <option
                      v-for="location in locations"
                      :value="location.id"
                      :key="location.id"
                      :selected="locationIsSelected(location.id)"
                    >
                      {{ location.name }}
                    </option>
                  </FormSelect>
                </div>
              </div>
            </div>

            <!-- package selection -->
            <div class="row mb-4">
              <div class="col-md-12">
                <label for="select-package">{{ $t("global.select-your-package") }} *</label>
                <div id="select-package">
                  <FormPackageSelect
                    :value="fields.packageId"
                    @input="onPackageSelect"
                    :packages="packages"
                    :plan-term="1"
                    :disabled="isSubmitting"
                  />
                </div>
              </div>
            </div>

            <!-- enter hostname -->
            <div class="row mb-4">
              <div class="col-md-12">
                <FormInput
                  name="hostname"
                  v-model="fields.hostname"
                  tes-iId="hostname"
                  :label="`${$t('global.hostname')} *`"
                  :errors="validation.fields.hostname.errors"
                  :dirty="validation.fields.hostname.dirty"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <label for="select-os">{{ $t("global.select-os") }} *</label>
                <div id="select-os">
                  <FormOSSelect
                    @input="onOSSelect"
                    @addonSelect="onAddonSelect"
                    :distributions="['centos', 'fedora', 'ubuntu']"
                    :oses="oses"
                    :addons="availableAddons"
                    :disabled="!fields.packageId"
                  />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <!-- choose access type -->
                <div class="row mb-4">
                  <div class="col-md-12">
                    <FormSelect
                      name="access"
                      v-model="fields.accessType"
                      :label="$t('global.select-access-type')"
                    >
                      <option value="password" :selected="true">
                        {{ $t("global.use-password") }}
                      </option>
                      <option value="ssh-key">{{ $t("global.use-ssh-key") }}</option>
                    </FormSelect>
                    <div v-if="fields.accessType === 'password'" class="text-secondary">
                      {{ $t("global.password-generation-notice") }}
                    </div>
                  </div>
                </div>

                <!-- choose or create ssh key -->
                <div class="row mb-4" v-if="showSSHKeyList">
                  <div class="col-md-12">
                    <label for="select-ssh-key">{{ $t("global.select-ssh-keys") }}</label>
                    <div id="select-ssh-key">
                      <Btn
                        v-if="showSSHKeyList"
                        variant="primary"
                        size="block"
                        class="my-2"
                        :disabled="isSubmitting"
                        :loading="isSubmitting"
                        @click="addSSHKey"
                        data-test="add-ssh-key"
                      >
                        {{ $t("global.add-ssh-key") }}
                      </Btn>
                      <div class="my-4">
                        <LoadingState v-if="sshKeysStatus.isLoading" />
                        <template v-else>
                          <FormCheckbox
                            v-for="(sshKey, index) in sshKeys"
                            :key="index"
                            :label="sshKey.name"
                            @change="(checked) => onSSHKeySelect(sshKey, checked)"
                          />
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>

      <div class="col-md-4">
        <Card :disable-title="true" :height100="false">
          <LoadingState v-if="summaryLoading" />
          <CheckoutCloudServerSummary
            v-else
            :title="!selectedPackage ? $t('global.your-cloud-server') : selectedPackage.name"
            :package="selectedPackage"
            :location="selectedLocation"
            :distribution="osType"
            :access-type="fields.accessType"
            :hostname="fields.hostname"
            :addons="selectedAddons"
            term="monthly"
            :total="monthlyTotal"
            :tax="tax"
            :hourlyTotal="hourlyTotal"
          />
          <div class="text-right mt-4">
            <Btn
              variant="primary"
              :disabled="isSubmitting"
              :loading="isSubmitting"
              @click="submit"
              data-test="submit"
            >
              {{ $t("global.submit") }}
            </Btn>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import AddSshKeyModal from "@/components/smart/modals/AddSshKey";
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import CheckoutCloudServerSummary from "@/components/display/checkout/CheckoutCloudServerSummary";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormOSSelect from "@/components/display/form/FormOSSelect";
import FormPackageSelect from "@/components/display/form/FormPackageSelect";
import FormSelect from "@/components/display/form/FormSelect";
import { fromServer } from "@/utils/translations";
import LoadingState from "@/components/display/LoadingState";
import PageTitle from "@/components/display/PageTitle";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { cloudServerAdd as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "CloudServerAddPage",
  components: {
    FormPackageSelect,
    Card,
    LoadingState,
    PageTitle,
    FormOSSelect,
    FormSelect,
    FormCheckbox,
    FormInput,
    Btn,
    CheckoutCloudServerSummary,
  },
  setup(props, { root }) {
    const isSubmitting = ref(false);
    const packages = computed(() => store.getters["cloudServerPackages/getList"]);
    const packagesStatus = computed(() => store.getters["cloudServerPackages/getStatus"]);
    const locations = computed(() => store.getters["locations/getList"]);
    const locationsStatus = computed(() => store.getters["locations/getStatus"]);
    const oses = computed(() => store.getters["os/getList"]);
    const osStatus = computed(() => store.getters["os/getStatus"]);
    const sshKeys = computed(() => store.getters["sshKeys/getList"]);
    const sshKeysStatus = computed(() => store.getters["sshKeys/getStatus"]);
    const accessTypes = ["password", "ssh-key"];

    const fields = reactive({
      locationId: null,
      packageId: null,
      osId: null,
      addonIds: [],
      accessType: accessTypes[0],
      sshKeyIds: [],
      hostname: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        locationId: {
          required: root.$t("validation.location-required"),
        },
        packageId: {
          required: root.$t("validation.package-required"),
        },
        hostname: {
          required: root.$t("validation.hostname-required"),
        },
      }
    );

    const availableAddons = computed(() => {
      if (!fields.packageId) {
        return [];
      }
      const pack = packages.value.find(({ id }) => id === fields.packageId);
      return pack.addons;
    });
    const selectedAddons = computed(() => {
      return availableAddons.value.filter((addon) => fields.addonIds.includes(addon.id));
    });

    const showSSHKeyList = computed(() => fields.accessType === "ssh-key");
    const tax = ref(0);
    const calculateTax = () => {
      taxLoading.value = true;
      store
        .dispatch("cloudServers/calculateTax", {
          packageId: fields.packageId,
          addonIds: fields.addonIds,
          term: "monthly",
        })
        .then((taxAmount) => {
          tax.value = taxAmount;
        })
        .finally(() => {
          taxLoading.value = false;
        });
    };

    const onPackageSelect = (packageId) => {
      fields.addonIds = [];
      fields.packageId = packageId;
      calculateTax();
    };

    const onOSSelect = (osId) => {
      fields.addonIds = [];
      fields.osId = osId;
    };

    const onAddonSelect = (addonIds) => {
      fields.addonIds = addonIds;
    };

    const onSSHKeySelect = ({ id }, checked) => {
      const index = fields.sshKeyIds.findIndex((key) => key.id === id);
      // add or remove key from list depending on whether checkbox is checked
      if (checked && index === -1) {
        fields.sshKeyIds.push(id);
      } else {
        fields.sshKeyIds.splice(index, 1);
      }
    };

    const addSSHKey = () => {
      store.dispatch("modals/open", {
        template: AddSshKeyModal,
      });
    };

    const locationIsSelected = (id) => id === fields.locationId;

    const submit = () => {
      isSubmitting.value = true;
      validation
        .validate()
        .then(() =>
          store
            .dispatch("cloudServers/create", {
              locationId: fields.locationId,
              hostname: fields.hostname,
              osId: fields.osId,
              packageId: fields.packageId,
              sshKeyIds: fields.sshKeyIds,
              addonIds: fields.addonIds,
              usePassword: fields.accessType === "password",
            })
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "success",
                message: root.$t("toasts.cloud-server-create-success", {
                  hostname: fields.hostname,
                }),
              });
              root.$router.push({ name: "cloud-servers" });
            })
            .catch((e) => {
              const errors = fromServer(
                e.request.response.errors.map(
                  ({ message, reference }) => `${message}: ${reference}`
                )
              );
              errors.forEach((error) => {
                store.dispatch("toasts/addToast", {
                  variant: "error",
                  message: error,
                });
              });
            })
        )
        .finally(() => {
          isSubmitting.value = false;
        });
    };

    const loading = ref(true);
    const taxLoading = ref(false);
    const summaryLoading = computed(() => loading.value || taxLoading.value);
    const selectedPackage = computed(() =>
      packages.value.find(({ id }) => id === fields.packageId)
    );
    const selectedLocation = computed(() =>
      locations.value.find(({ id }) => id === Number(fields.locationId))
    );
    const selectedOs = computed(() => oses.value.find(({ id }) => id === Number(fields.osId)));
    // formatted version name
    const osType = computed(() => {
      if (!selectedOs.value) {
        return null;
      }
      return `${selectedOs.value.os.distribution} ${selectedOs.value.os.version} ${selectedOs.value.os.architecture}`;
    });
    const monthlyAddonTotal = computed(() => {
      let sum = 0;
      selectedAddons.value.forEach(({ monthlyFee }) => {
        sum += monthlyFee;
      });
      return sum;
    });
    const monthlyTotal = computed(
      () => (selectedPackage.value?.monthlyFee || 0) + monthlyAddonTotal.value + tax.value
    );
    const hourlyTotal = computed(() => monthlyTotal.value / 672);

    onMounted(() => {
      Promise.all([
        store.dispatch("cloudServerPackages/getList"),
        store.dispatch("locations/getList"),
        store.dispatch("os/getList"),
        store.dispatch("sshKeys/getList"),
      ]).finally(() => {
        loading.value = false;
      });
    });

    return {
      loading,
      summaryLoading,
      selectedPackage,
      selectedLocation,
      monthlyTotal,
      hourlyTotal,
      fields,
      packages,
      packagesStatus,
      locations,
      locationsStatus,
      sshKeys,
      onPackageSelect,
      onOSSelect,
      availableAddons,
      selectedAddons,
      onAddonSelect,
      isSubmitting,
      oses,
      osStatus,
      osType,
      showSSHKeyList,
      onSSHKeySelect,
      sshKeysStatus,
      addSSHKey,
      locationIsSelected,
      submit,
      validation,
      tax,
    };
  },
};
</script>
